// Helper functions to generate structured data (JSON-LD)

export const generateOrganizationSchema = (settings = {}) => {
  const companyName = settings?.companyName || 'Store';
  const siteUrl = settings?.websiteUrl || process.env.REACT_APP_SITE_URL || window.location.origin;
  const description = settings?.joinOurJourney?.description || 'Online store';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: companyName,
    description: description,
    url: siteUrl,
  };
  
  if (settings?.logo) {
    schema.logo = settings.logo.startsWith('http') ? settings.logo : `${siteUrl}${settings.logo}`;
  }
  
  if (settings?.phone || settings?.whatsappNumber) {
    schema.contactPoint = {
      '@type': 'ContactPoint',
      telephone: settings.phone || settings.whatsappNumber,
      contactType: 'Customer Service',
      areaServed: settings?.address?.country || 'IN',
      availableLanguage: ['en'],
    };
  }
  
  const sameAs = [];
  if (settings?.socialMedia?.facebook) sameAs.push(settings.socialMedia.facebook);
  if (settings?.socialMedia?.instagram) sameAs.push(settings.socialMedia.instagram);
  if (settings?.socialMedia?.twitter) sameAs.push(settings.socialMedia.twitter);
  if (settings?.socialMedia?.linkedin) sameAs.push(settings.socialMedia.linkedin);
  
  if (sameAs.length > 0) {
    schema.sameAs = sameAs;
  }
  
  return schema;
};

export const generateProductSchema = (product, settings = {}) => {
  const companyName = settings?.companyName || 'Store';
  const siteUrl = settings?.websiteUrl || process.env.REACT_APP_SITE_URL || window.location.origin;
  const price = product.discountedPrice || product.price;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.images && product.images.length > 0 ? product.images : [],
    sku: product._id,
    brand: {
      '@type': 'Brand',
      name: companyName,
    },
    offers: {
      '@type': 'Offer',
      url: `${siteUrl}/products/${product._id}`,
      priceCurrency: product.currency || 'INR',
      price: price,
      availability: product.stock > 0 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: companyName,
      },
    },
    category: product.category?.name || 'Product',
  };
};

export const generateArticleSchema = (blog, settings = {}) => {
  const companyName = settings?.companyName || 'Store';
  const siteUrl = settings?.websiteUrl || process.env.REACT_APP_SITE_URL || window.location.origin;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: blog.title,
    description: blog.excerpt || blog.title,
    image: blog.image ? [blog.image] : [],
    datePublished: blog.publishedAt || blog.createdAt,
    dateModified: blog.updatedAt || blog.publishedAt || blog.createdAt,
    author: {
      '@type': 'Person',
      name: blog.author || 'Admin',
    },
    publisher: {
      '@type': 'Organization',
      name: companyName,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/blog/${blog._id}`,
    },
    articleSection: blog.category,
    keywords: blog.tags?.join(', ') || blog.category,
  };
  
  if (settings?.logo) {
    schema.publisher.logo = {
      '@type': 'ImageObject',
      url: settings.logo.startsWith('http') ? settings.logo : `${siteUrl}${settings.logo}`,
    };
  }
  
  return schema;
};

export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const generateWebsiteSchema = (settings = {}) => {
  const companyName = settings?.companyName || 'Store';
  const siteUrl = settings?.websiteUrl || process.env.REACT_APP_SITE_URL || window.location.origin;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: companyName,
    url: siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/products?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
};


